import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@parrot-software/design-system';
import type { InvoiceDataViewDto } from '@subscriptions/application/dtos';
import { getInvoiceDisplayName } from '@subscriptions/presentation/utils';

/**
 * Props for DeleteInvoiceDataDialog component
 */
export interface DeleteInvoiceDataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceData?: InvoiceDataViewDto;
  onConfirm: () => void;
  isLoading?: boolean;
}

/**
 * Confirmation dialog for deleting invoice data.
 * Shows a warning message and requires explicit confirmation.
 */
export const DeleteInvoiceDataDialog = ({
  open,
  onOpenChange,
  invoiceData,
  onConfirm,
  isLoading = false,
}: DeleteInvoiceDataDialogProps): React.ReactElement => {
  const { t } = useTranslation('invoiceData');
  const displayName = getInvoiceDisplayName(invoiceData);

  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent className="max-w-[451px] font-sans">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('deleteConfirm.title')}</AlertDialogTitle>
          <AlertDialogDescription className="space-y-1">
            <p className="text-muted-foreground wrap-anywhere">
              {t('deleteConfirm.description', { businessName: displayName })}
            </p>
            <p className="font-semibold text-muted-foreground">{t('deleteConfirm.warning')}</p>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>{t('deleteConfirm.cancelButton')}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={isLoading}
            onClick={onConfirm}
          >
            {isLoading ? t('deleteConfirm.loadingButton') : t('deleteConfirm.confirmButton')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
